import Swiper from 'swiper';
import { Navigation, Pagination, Thumbs, Scrollbar, Autoplay, EffectFade, Keyboard } from 'swiper/modules';

import 'swiper/css';
import '../../styles/components/swiper.scss';

import { onBreakpoint } from '../utils/breakpoints';

/**
 * config attribute accepts any of the swiper config.
 *
 * Custom config:
 *  - onBreakpoint {String} : will listen to a breakpoint. Swiper will be built when the
 *    breakpoint is entered and destroyed when the breakpoint is left.
 *    can be "mobile", "tablet", "desktop", "widescreen", "tabletDown", "desktopDown"
 *
 *  - onOverflow {Boolean} : Swiper will be built when the slides gets larger than the
 *    container.
 */
class SwiperElement extends HTMLElement {
  static get observedAttributes() { return ['config']; }

  constructor() {
    super();

    this.modules = [Navigation, Pagination, Thumbs, Scrollbar, Autoplay, EffectFade, Keyboard];

    this.onOverflowFunction = this.onOverflow.bind(this);

    this.prevEl = this.querySelector(':scope .swiper-button-prev') || this.parentElement.querySelector(':scope > .swiper-button-prev');
    this.nextEl = this.querySelector(':scope .swiper-button-next') || this.parentElement.querySelector(':scope > .swiper-button-next');

    this.paginationEl = this.querySelector(':scope .swiper-pagination') || this.parentElement.querySelector(':scope > .swiper-pagination');

    this.initializeSwiper();
  }

  static get observedAttributes() { return ['in-view']; }

  attributeChangedCallback(name, oldValue, newValue) {
    if (this.hasAttribute('in-view')) {
      this.inView();
    }
  }

  initializeSwiper() {
    this.config = {
      modules: this.modules,
      observer: true,
      observeParents: true,
      watchSlidesVisibility: true,
      navigation: this.prevEl ? {
        nextEl: this.nextEl,
        prevEl: this.prevEl,
      } : false,
      pagination: this.paginationEl ? {
        el: this.paginationEl,
        clickable: true,
      } : false
    }

    if (this.hasAttribute('config')) {
      this.config = {
        ...this.config,
        ...JSON.parse(this.getAttribute('config'))
      }

      if (this.config.disabled) {
        return;
      }

      if (this.config.thumbs) {
        //converts the querySelector to a swiper instance
        this.config.thumbs.swiper = document.querySelector(this.config.thumbs.swiper).swiper;
      }
    }

    if (this.config.onBreakpoint) {
      onBreakpoint(this.config.onBreakpoint, () => {
        this.swiper = new Swiper(this, this.config);
      }, () => {
        if (this.swiper) {
          this.swiper.destroy();
        }
      })
    } else if (this.config.onOverflow) {
      this.onOverflowFunction();
      window.addEventListener('resize', this.onOverflowFunction);
    } else {
      this.swiper = new Swiper(this, this.config);
    }
  }

  onOverflow() {
    const sliderInitialized = this.classList.contains('swiper-initialized');
    const slides = this.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide');

    let totalWidth = 0;
    slides.forEach(slide => {
      const slideComputedStyle = getComputedStyle(slide);
      totalWidth += slide.offsetWidth + parseFloat(slideComputedStyle.marginLeft) + parseFloat(slideComputedStyle.marginRight);
    });

    if (totalWidth > this.offsetWidth) {
      if (!sliderInitialized) {
        this.swiper = new Swiper(this, this.config);
      }
    } else if (this.swiper) {
      this.swiper.destroy();
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onOverflowFunction);
  }

  refreshOverflow() {
    this.onOverflow();
  }

  /**
   * fixes issues with onOverflow misfiring
   * when things are not fully loaded
   */
  inView() {
    if (this.config.onOverflow) {
      setTimeout(() => {
        this.onOverflow();
      }, 500);
    }
  }
}

try {
  customElements.define('swiper-container', SwiperElement);

  const options = {
    root: null,
    rootMargin: "150px"
  }

  const callback = (entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.setAttribute('in-view', '');
        observer.unobserve(entry.target);
      }
    });
  }

  const swiperObserver = new IntersectionObserver(callback, options);

  const swiperElements = document.querySelectorAll('swiper-container');
  if (swiperElements) {
    swiperElements.forEach(swiperElement => {
      swiperObserver.observe(swiperElement);
    });
  }
}
catch(e) {}
